// Atom-type color system for the knowledge tier (timeline spine + swatch + tint).
//
// Groups the real data-model types onto the design-handoff's 7-color base.
// `typeColor`/`typeTint` resolve any type (known or not) to a color/tint,
// falling back to reference gray.
//
// `TYPE_COLORS` is kept (same `Record<string,string>` shape) so existing
// consumers (FilterChips, FeedPage) keep compiling — it is now derived from
// the same table and covers every real type.

export interface TypePalette {
  color: string;
  tint: string;
}

const REFERENCE_GRAY: TypePalette = { color: "#64748b", tint: "#eef1f4" };

// Real data-model types → palette. Grouped per REVAMP-PLAN "Atom-type colors".
export const TYPE_PALETTE: Record<string, TypePalette> = {
  // indigo — decisions
  decision: { color: "#4f46e5", tint: "#edeefb" },
  // red — incidents / postmortems
  incident: { color: "#dc2626", tint: "#fcebeb" },
  postmortem: { color: "#dc2626", tint: "#fcebeb" },
  // cyan — runbooks / how-to
  runbook: { color: "#0891b2", tint: "#e3f4f9" },
  "how-to": { color: "#0891b2", tint: "#e3f4f9" },
  // green — conventions / architecture / config
  convention: { color: "#16a34a", tint: "#e8f6ed" },
  architecture: { color: "#16a34a", tint: "#e8f6ed" },
  config: { color: "#16a34a", tint: "#e8f6ed" },
  // amber — notes / meeting-note / client-note / observation
  note: { color: "#d97706", tint: "#fbf0e0" },
  "meeting-note": { color: "#d97706", tint: "#fbf0e0" },
  "client-note": { color: "#d97706", tint: "#fbf0e0" },
  observation: { color: "#d97706", tint: "#fbf0e0" },
  // slate (reference gray) — reference / task / idea / workflow
  reference: { color: "#64748b", tint: "#eef1f4" },
  task: { color: "#64748b", tint: "#eef1f4" },
  idea: { color: "#64748b", tint: "#eef1f4" },
  workflow: { color: "#64748b", tint: "#eef1f4" },
  // purple — questions / person notes
  question: { color: "#9333ea", tint: "#f4eafc" },
  person_note: { color: "#9333ea", tint: "#f4eafc" },
};

/** Spine / swatch / label color for an atom type. Unknown → reference gray. */
export function typeColor(type: string | undefined | null): string {
  return (type && TYPE_PALETTE[type]?.color) || REFERENCE_GRAY.color;
}

/** Soft tint background for an atom type. Unknown → reference gray tint. */
export function typeTint(type: string | undefined | null): string {
  return (type && TYPE_PALETTE[type]?.tint) || REFERENCE_GRAY.tint;
}

// Back-compat: flat color map. Derived from TYPE_PALETTE; consumers index by
// type and fall back to gray themselves, so this stays a plain string record.
export const TYPE_COLORS: Record<string, string> = Object.fromEntries(
  Object.entries(TYPE_PALETTE).map(([k, v]) => [k, v.color]),
);
